import { Component, ElementRef, inject, Signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CategorySelected, CategorySelectorComponent } from "../../component/layout/element/category-selector";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { SavedSearch, SearchParams, SearchSortKey } from '../../../../globalTypes/search.types';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { searchActions, searchFeature } from '../../store/search.store';
import { JsonPipe } from '@angular/common';
import { CategoryPublic } from '../../../../globalTypes/category.types';
import { categoriesFeature } from '../../store/category.store';
import { filter, lastValueFrom, take } from 'rxjs';
import { accountFeature } from '../../store/account.store';
import { MatIcon } from "@angular/material/icon";
import { authUserFeature } from '../../store/authUser.store';
import { AuthService } from '../../services/auth.service';
import { ListingSearchComponent } from "../../component/layout/element/listing-search.component";


@Component({
  selector: 'msh-search-side-bar',
  standalone: true,
  imports: [FormsModule, CategorySelectorComponent, MatSlideToggle, MatIcon, ListingSearchComponent],
  template: `
<div class=" md:max-w-80 w-full bg-back p-4 space-y-6 text-sm text-navy-900">

  <!-- Top row: Watch List + Sort -->
  <div class="flex justify-between items-center">
    <!-- [ ] Watch List firebase -->
    @if(locationId){
      <div
        (click)="toggleWatchList()"
        class="flex cursor-pointer items-center space-x-1 text-orange-400 hover:underline"
      >
        <svg xmlns="http://www.w3.org/2000/svg"
            [attr.fill]="filter().onlyFavorited ? 'currentColor' : 'none'"
            viewBox="0 0 24 24" stroke="currentColor"
            class="w-5 h-5 text-orange-400">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M11.995 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 
              2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 
              4.495 2.09C13.09 3.81 14.76 3 
              16.5 3 19.58 3 22 5.42 22 8.5c0 
              3.78-3.4 6.86-8.545 11.54l-1.46 1.31z"/>
        </svg>
        <span>Watch List</span>
      </div>
    }
    <div [class]="'md:hidden ' + (mobileMenuOpen ? 'text-orange-400 ' : '')" (click)="toggleMobileMenu()"><mat-icon>sort</mat-icon></div>
    <!-- [x] Sort firebase -->
    <div class="relative">
      <div
        (click)="toggleSortMenu()"
        class="text-navy-900  cursor-pointer"
      >
        Sort: {{ selectedSort }}
      </div>
      @if (sortMenuOpen) {
        <div
          class="absolute  min-w-56 right-0 mt-2 bg-back border border-gray-200 rounded shadow-md z-10 origin-top transition transform scale-y-100 animate-fadeIn"
        >
          <ul>
            @for (option of sortOptions; track option) {
              <li>
                <div
                  (click)="selectSort(option)"
                  class="block w-full px-4 py-2 cursor-pointer text-left hover:bg-copy/15"
                >
                  {{ option }}
                </div>
              </li>
            }
          </ul>
        </div>
      }
    </div>
  </div>

  <!-- [ ] Price filter firebase -->
  <div [class]="(mobileMenuOpen ? 'flex' : 'hidden md:flex') +' !mt-0 flex-col gap-4 overflow-auto max-h-[70vh]'">

  <msh-listing-search class=" md:hidden "></msh-listing-search>
    <div>
      <div class="flex items-center space-x-2">
        <h3 class="font-semibold">Price</h3>
        <input
          type="number"
          placeholder="Min"
          [value]="filter().priceMin"
          (change)="onPriceChange('priceMin', $event)"
          style="width:70px"
          class="w-10 border rounded !mb-3 py-1 text-sm"
        />
        <span>-</span>
        <input
          type="number"
          placeholder="Max"
          [value]="filter().priceMax"
          [min]="filter().priceMin || 0"
          (change)="onPriceChange('priceMax', $event)"
          style="width:70px"
          class="w-10 border rounded !mb-3 py-1 text-sm"
        />
        <div (click)="clearPrices()" class="text-gray-400  cursor-pointer hover:underline">Clear</div>
      </div>
    </div>

    <!-- [ ] Saved searches firebase - current search (after load + X seconds, save search) -->
    @if(locationId){
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="font-semibold">Saved Searches</h3>
          <div (click)="toggleSaveInput()" class="text-orange-400 font-light cursor-pointer">+ Save this search</div>
        </div>

        @if (showSaveInput) { <!-- TODO make Save search input and button into one line (padding in input) -->
          <div class="flex space-x-2 mb-2 items-center transition-all duration-300 ease-in-out">
            <input
              id="new-search-name-input-id"
              type="text"
              [(ngModel)]="newSearchName"
              placeholder="Search name"
              class="flex-1 border rounded px-2 py-1 !mt-0 !mb-0"
              (keyup)="onNewSearchNameKeyDown($event)"
            />
            @if(newSearchName.trim() === ''){
              <mat-icon (click)="toggleSaveInput()" class="text-copy opacity-50 hover:text-orange-400 cursor-pointer">close_small</mat-icon>
            }@else {
              <mat-icon (click)="saveSearch()" class="text-copy hover:text-orange-400 cursor-pointer">check_small</mat-icon>
            }
          </div>
        }

        <ul>
          @for (s of savedSearches; track s.name) {
            <li class="flex justify-between items-center py-1 group gap-1">
              <div (click)="setSearchFromSaved(s)" class=" max-w-44 cursor-pointer rounded hover:bg-orange-300 border-orange-400 border p-2">{{ s.name }}</div>
              <span class="text-gray-400 text-xs group-hover:hidden">{{ s.date }}</span>
              <div (click)="deleteSavedSearch(s)" class="hidden group-hover:inline text-copy/50 hover:text-orange-400 text-xs cursor-pointer"><mat-icon>close_small</mat-icon></div>
            </li>
          }
        </ul>
      </div>
    }
    <!-- Categories -->
    <div>
      <h3 class="font-semibold mb-2">Categories</h3>
      @if(locationId){
        <div class="flex flex-wrap gap-2 mb-4">
          @for (c of categories; track c) {
            <span
              class="flex items-center bg-orange-400/20 text-orange-400 rounded-full px-3 py-1 text-xs"
            >
              {{ c }}
              <div
                (click)="deleteCategory(c)"
                class="ml-1 text-orange-400 hover:text-orange-800 text-xs cursor-pointer"
              >
                ✕
              </div>
            </span>
          }
        </div>
      }
      <msh-category-selector
        [selectedCategoryNames]="selectedCategoryNames"
        (onSelectCategory)="onSelectCategory($event)"
      ></msh-category-selector>
    </div>

    <!-- Toggle deleted listings -->
    @if(locationId){
      <div class="flex items-center space-x-2 border-t pt-4 opacity-75">
        <mat-slide-toggle id="deletedToggle" (toggleChange)="toggleShowOnlyMyListings()" [checked]="!!filter().onlyListingsFromLocationId"></mat-slide-toggle>
        <label (click)="toggleShowOnlyMyListings()" for="deletedToggle" class="text-navy-900 font-light cursor-pointer">
          View all my listings
        </label>
      </div>
    }
  </div>
</div>

  `,
  styles: [`
    .animate-fadeIn {
      animation: fadeIn 0.2s ease-out;
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: scaleY(0.95); }
      to { opacity: 1; transform: scaleY(1); }
    }
  `]
})
export class SearchSideBarComponent {
  filter: Signal<SearchParams> = toSignal(inject(Store).select(searchFeature.selectParams), { initialValue: { sortDir: "ASC", sortKey: "createdAt" } as SearchParams});
  watchListEnabled = false;
  mobileMenuOpen = false;
  selectedCategoryNames: string[] = [] // TODO add saved searches store
  selectedSort?: string;
  sortMenuOpen = false;

  minPrice: string = '';
  maxPrice: string = '';

  showSaveInput = false;
  newSearchName: string = '';
  locationId: string = '';
  savedSearches: SavedSearch[] = [
    // { name: 'My first search', date: '2025-09-24' },
    // { name: 'Cheap listings', date: '2025-09-23' }
  ];

  sortMapping = {
    'createdAt_DESC': 'Newest',
    'createdAt_ASC': 'Oldest',
    'lowestPrice_ASC': '$',
    'lowestPrice_DESC': '$$$',
    'searchContents_ASC': 'A -> Z',
    'searchContents_DESC': 'Z -> A',
  }
  sortOptions = Object.values(this.sortMapping)

  categories: string[] = [];
  availableCategories: CategoryPublic[] = []

  viewDeleted = false;

  constructor(
    private store: Store,
    private authService: AuthService,
  ){
    this.store.select(categoriesFeature.selectCategoriesState).pipe(filter(c=>!!c.entities), take(1)).subscribe(state => {
      this.availableCategories = Object.values(state.entities) as CategoryPublic[];
      console.log({availableCategories: this.availableCategories})
    })
    this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1)).subscribe(location => {
      this.locationId = location?.id;
    })
    this.store.select(searchFeature.selectParams).pipe(take(1)).subscribe(params => {
      if(params.categoryIds && params.categoryIds.length > 0){
        const selectedCats = this.availableCategories.filter(c => params.categoryIds?.includes(c.id));
        this.categories = selectedCats.map(c => c.name || '');
        this.selectedCategoryNames = this.categories;
      }
      // set selected sort
      const key: keyof typeof this.sortMapping = params.sortKey + '_' + params.sortDir as keyof typeof this.sortMapping;
      this.selectedSort = this.sortMapping[key] || 'Newest'
    });
    this.store.select(authUserFeature.selectSavedSearches).pipe(take(1)).subscribe(searches =>{
      this.savedSearches = searches
    })
  }

  toggleWatchList() {
    this.store.dispatch(searchActions.toggleFavorited())
  }
  toggleShowOnlyMyListings(){
    this.store.dispatch(searchActions.toggleOnlyListingsFromLocationId(this.locationId))
  }
  toggleMobileMenu(){
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }
  toggleSortMenu() {
    this.sortMenuOpen = !this.sortMenuOpen;
  }

  selectSort(option: string) {
    this.selectedSort = option;
    this.sortMenuOpen = false;
    const mapping = Object.keys(this.sortMapping).find(key => this.sortMapping[key as keyof typeof this.sortMapping] === option)
    const [sortKey, sortDir] = mapping ? mapping.split('_') : ['createdAt', 'DESC'];
    this.store.dispatch(searchActions.adjustSort( sortKey as SearchSortKey, sortDir as 'ASC' | 'DESC'))
  }

  onPriceChange(field: 'priceMin' | 'priceMax', ev: any) {
    console.log({ev})
    const num = ev.target.value;
    if(field === 'priceMin'){
      this.store.dispatch(searchActions.addMinPrice(Number(num)));
    } else {
      this.store.dispatch(searchActions.addMaxPrice(Number(num)));
    }
  }

  clearPrices() {
    this.store.dispatch(searchActions.addMinPrice(undefined));
    this.store.dispatch(searchActions.addMaxPrice(undefined));
  }

  toggleSaveInput() {
    setTimeout(() => {
      const el = window.document.getElementById("new-search-name-input-id")
      el?.focus();
    },0)
    this.showSaveInput = !this.showSaveInput;
    this.newSearchName = '';
  }

  setSearchFromSaved(saved: SavedSearch){
    this.store.dispatch(searchActions.addSearch(saved.params))
  }

  onNewSearchNameKeyDown(ev: KeyboardEvent){
    console.log({ev})
    if(ev.code === 'Enter' && this.newSearchName){
      this.saveSearch()
    }
  }

  async saveSearch() {
    const newSearch: SavedSearch = {
      name: this.newSearchName.trim(),
      date: new Date().toISOString().split('T')[0],
      params: this.filter(),
    }
    let {preferences, uid} = await lastValueFrom(this.store.select(authUserFeature.selectPreferences).pipe(take(1)))
    if(!uid) return;
    preferences = JSON.parse(JSON.stringify(preferences)) || {};
    if(!preferences.savedSearches) preferences.savedSearches = [];
    preferences.savedSearches.unshift(newSearch);
    console.log({preferences: preferences});
    
    this.authService.saveUser({id: uid, preferences })
    this.savedSearches = preferences.savedSearches;
    this.newSearchName = '';
    this.showSaveInput = false;
    
  }

  async deleteSavedSearch(search: SavedSearch){
    let {preferences, uid} = await lastValueFrom(this.store.select(authUserFeature.selectPreferences).pipe(take(1)))
    if(!uid) return;
    preferences = JSON.parse(JSON.stringify(preferences)) || {};
    if(!preferences.savedSearches) preferences.savedSearches = [];
    preferences.savedSearches = preferences.savedSearches.filter((s:SavedSearch)=>s.name !== search.name);
    console.log({preferences: preferences});
    
    this.authService.saveUser({id: uid, preferences })
    this.savedSearches = preferences.savedSearches;
  }

  onSelectCategory(category: CategorySelected[]){
    this.categories = category.map(c => c.name);
    this.store.dispatch(searchActions.setCategory(category.map(c => c.id)))
  }

  deleteCategory(category: string) {
    this.categories = this.categories.filter(c => c !== category);
    this.selectedCategoryNames = this.categories
    const deletedId = this.availableCategories.find(c => c.name === category)?.id;
    const curr = this.filter().categoryIds || [];
    console.log({deletedId, category, curr, availableCategories: this.availableCategories})
    this.store.dispatch(searchActions.setCategory(curr.filter(id => deletedId !== id)))
  }
}
